const CHECKOUT_DRAFT_STORAGE_KEY = 'dasoonegg-checkout-draft';

export interface CheckoutDraft<TFormData, TCheckoutMode extends string = string> {
  formData: Partial<TFormData>;
  reviewCustomerInfo: TFormData | null;
  currentStep: number;
  checkoutMode: TCheckoutMode;
}

const isStorageAvailable = (): boolean =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

export const loadCheckoutDraft = <TFormData, TCheckoutMode extends string = string>():
  | CheckoutDraft<TFormData, TCheckoutMode>
  | null => {
  if (!isStorageAvailable()) {
    return null;
  }

  const rawDraft = window.sessionStorage.getItem(CHECKOUT_DRAFT_STORAGE_KEY);
  if (!rawDraft) {
    return null;
  }

  try {
    const parsedDraft = JSON.parse(rawDraft) as Partial<CheckoutDraft<TFormData, TCheckoutMode>>;

    if (!parsedDraft || typeof parsedDraft !== 'object') {
      return null;
    }

    return {
      formData: parsedDraft.formData ?? {},
      reviewCustomerInfo: parsedDraft.reviewCustomerInfo ?? null,
      currentStep: typeof parsedDraft.currentStep === 'number' ? parsedDraft.currentStep : 0,
      checkoutMode: parsedDraft.checkoutMode as TCheckoutMode,
    };
  } catch (error) {
    console.warn('[CheckoutDraftStorage] Failed to parse saved checkout draft:', error);
    window.sessionStorage.removeItem(CHECKOUT_DRAFT_STORAGE_KEY);
    return null;
  }
};

export const saveCheckoutDraft = <TFormData, TCheckoutMode extends string = string>(
  draft: CheckoutDraft<TFormData, TCheckoutMode>
): void => {
  if (!isStorageAvailable()) {
    return;
  }

  window.sessionStorage.setItem(CHECKOUT_DRAFT_STORAGE_KEY, JSON.stringify(draft));
};

export const clearCheckoutDraft = (): void => {
  if (!isStorageAvailable()) {
    return;
  }

  window.sessionStorage.removeItem(CHECKOUT_DRAFT_STORAGE_KEY);
};
